import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { SITE } from "@/lib/site";

export const Container = ({ className, children, ...rest }) => (
  <div {...rest} className={cn("mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12", className)}>{children}</div>
);

export const Section = ({ className, children, id, ...rest }) => (
  <section id={id} {...rest} className={cn("relative py-20 lg:py-28 overflow-x-clip", className)}>{children}</section>
);

export const Eyebrow = ({ children, className }) => <div className={cn("eyebrow", className)}>{children}</div>;

export const SectionHeader = ({ eyebrow, title, desc, align = "left", className, testId }) => (
  <div className={cn("max-w-3xl", align === "center" && "mx-auto text-center", className)} data-testid={testId}>
    {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
    <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-mk-text text-balance leading-[1.05]">{title}</h2>
    {desc && <p className="mt-5 text-base md:text-lg text-mk-dim leading-relaxed">{desc}</p>}
  </div>
);

export const Reveal = ({ children, delay = 0, y = 24, className, once = true, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once, margin: "-80px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);

export const Stagger = ({ children, className, step = 0.08 }) => (
  <div className={className}>
    {React.Children.map(children, (child, i) => (
      <Reveal delay={i * step}>{child}</Reveal>
    ))}
  </div>
);

export const CountUp = ({ value, prefix = "", suffix = "", decimals = 0, duration = 1.6, display, className }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf, start;
    const tick = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setN(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return (
    <span ref={ref} className={cn("font-mono tabular-nums", className)}>
      {display && inView && n >= value ? display : `${prefix}${n.toFixed(decimals)}${suffix}`}
    </span>
  );
};

export const CTAButton = ({ to, href, children, variant = "lime", className, testId, ...rest }) => {
  const cls = cn(variant === "lime" ? "btn-lime" : variant === "blue" ? "btn-blue" : "btn-ghost", className);
  if (href) return <a href={href} target="_blank" rel="noreferrer" className={cls} data-testid={testId} {...rest}>{children}</a>;
  return <Link to={to} className={cls} data-testid={testId} {...rest}>{children}</Link>;
};

export const Logo = ({ className, withTagline = false, size = "h-8" }) => (
  <span className={cn("inline-flex items-center gap-2.5", className)}>
    <img src="/fixlr-icon.png" alt="Fixlr" className={cn(size, "w-auto rounded-lg")} />
    <span className="flex flex-col leading-none">
      <span className="font-display font-bold text-lg tracking-tight text-mk-text">Fi<span className="text-mk-blue">x</span>lr<span className="text-mk-lime">.</span></span>
      {withTagline && <span className="mt-0.5 text-[10px] text-mk-dim tracking-wide">Fix more, Manage less</span>}
    </span>
  </span>
);

export const Kicker = ({ children, tone = "lime", className }) => (
  <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em]", tone === "lime" ? "border-mk-lime/40 bg-mk-lime/10 text-mk-lime" : "border-mk-blue/50 bg-mk-blue/15 text-[#8db6ff]", className)}>{children}</span>
);

const ensureMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) { el = document.createElement(selector.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
};

export const useSeo = ({ title, description, path, image, type = "website", noindex = false } = {}) => {
  useEffect(() => {
    const base = SITE.canonicalUrl;
    const fullTitle = title ? `${title} | Fixlr` : "Fixlr | Fix more, Manage less — Maintenance operations platform";
    const desc = description || SITE.defaultDescription;
    const url = `${base}${path || window.location.pathname}`;
    const img = /^https?:/.test(image || "") ? image : `${base}${image || "/og/default.png"}`;
    document.title = fullTitle;
    ensureMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex,nofollow" : "index,follow" });
    ensureMeta('meta[name="description"]', { name: "description", content: desc });
    ensureMeta('link[rel="canonical"]', { rel: "canonical", href: url });
    ensureMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    ensureMeta('meta[property="og:description"]', { property: "og:description", content: desc });
    ensureMeta('meta[property="og:url"]', { property: "og:url", content: url });
    ensureMeta('meta[property="og:image"]', { property: "og:image", content: img });
    ensureMeta('meta[property="og:type"]', { property: "og:type", content: type });
    ensureMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "Fixlr" });
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description", content: desc });
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image", content: img });
  }, [title, description, path, image, type, noindex]);
};

export const usePageTitle = (title, description, image) => useSeo({ title, description, image });
